import { useCallback, useState, useRef, useEffect, ReactElement } from "react";
import { useSelector } from "react-redux";
import {
  AddressFieldWithSearchConfig,
  CountryFieldConfig,
} from "@evergiving/common/dist/es2015/form";

import { RootState } from "@/lib/redux/types";
import { PostalAddressKind } from "@evergiving/common/dist/es2015/pledge";
import { MelissaResponse } from "@/lib/misc/MelissaResponse";
import { SearchAddressField } from "@/lib/components/SearchAddressField";
import { MelissaAddressField } from "@/lib/components/MelissaAddressField";
import { FreeFormAddressComponent } from "@/lib/components/FreeFormAddressComponent";

export function AddressFieldWithSearch({
  locale,
  countryFieldConfig,
  addressFieldConfig,
  defaultCountryCode,
}: {
  locale: string;
  countryFieldConfig: CountryFieldConfig | undefined;
  addressFieldConfig: AddressFieldWithSearchConfig;
  defaultCountryCode: string;
}): ReactElement | null {
  const postalAddress = useSelector((state: RootState) => state.postalAddress);
  const currentCountryCode = useSelector(
    (state: RootState) => state.countryCode.value
  );

  const loadingCount = useRef<number>(0);
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const controller = useRef<AbortController | null>(null);

  const [results, setResults] = useState<MelissaResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [text, setText] = useState<string>("");

  const search = useCallback(
    async (searchText: string) => {
      if (timeout.current) {
        clearTimeout(timeout.current);
        timeout.current = null;
      }
      if (controller.current) {
        controller.current.abort();
        controller.current = null;
      }

      const query = searchText.trim();
      if (query.length > 0 && currentCountryCode) {
        const url = new URL(
          addressFieldConfig.melissa.addressCompletionEndpointUrl
        );
        url.search = new URLSearchParams({
          query,
          maxNumRecords: String(10),
          countryCode: currentCountryCode,
        }).toString();
        try {
          loadingCount.current += 1;
          setLoading(true);
          controller.current = new AbortController();
          const { signal } = controller.current;
          const result = await fetch(url.toString(), {
            signal,
          });
          const body = (await result.json()) as MelissaResponse;
          setResults(body);
        } catch (e) {
          if (e.name !== "AbortError") {
            console.warn(e);
          }
        } finally {
          loadingCount.current -= 1;
          if (loadingCount.current === 0) {
            setLoading(false);
          }
        }
      }
    },
    [
      addressFieldConfig.melissa.addressCompletionEndpointUrl,
      currentCountryCode,
    ]
  );

  const handleSearch = useCallback(async () => {
    await search(text);
  }, [text, search]);

  const handleClear = useCallback(() => {
    if (controller.current) {
      controller.current.abort();
      controller.current = null;
    }
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = null;
    }
    setText("");
    setResults(null);
  }, []);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target) {
        const newValue = event.target.value;
        if (newValue !== text) {
          setText(newValue);
          if (newValue.trim() !== text.trim()) {
            if (controller.current) {
              controller.current.abort();
              controller.current = null;
            }

            if (timeout.current) {
              clearTimeout(timeout.current);
            }

            timeout.current = setTimeout(() => search(newValue), 500);
          }
        }
      }
    },
    [text, search]
  );

  useEffect(() => {
    if (loadingCount.current === 0) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, []);

  if (postalAddress === undefined) {
    return (
      <SearchAddressField
        locale={locale}
        melissaConfig={addressFieldConfig.melissa}
        results={results}
        onChange={handleChange}
        onSearch={handleSearch}
        onClear={handleClear}
        loading={loading}
        value={text}
        defaultCountryCode={defaultCountryCode}
      />
    );
  }

  const countryName = currentCountryCode
    ? countryFieldConfig?.supportedCountryNameTexts[currentCountryCode]
    : undefined;

  if (postalAddress.kind === PostalAddressKind.Melissa) {
    return (
      <MelissaAddressField
        postalAddress={postalAddress}
        melissaAddressFieldConfig={addressFieldConfig.melissa}
        countryName={countryName}
      />
    );
  }

  if (postalAddress.kind === PostalAddressKind.ApplePay) {
    // This page isn't used for Apple Pay payments
    return null;
  }

  const freeFormAddressFieldConfig =
    (currentCountryCode
      ? addressFieldConfig.freeForm.byCountry[currentCountryCode]
      : undefined) ?? addressFieldConfig.freeForm.defaultConfig;

  return (
    <FreeFormAddressComponent
      freeFormAddressFieldConfig={freeFormAddressFieldConfig}
    />
  );
}
